import React from "react";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableField } from "../SortableField";
import { useEditorStore, DesignSettings, EditorField } from "../../../store";
import { usePolarisHash } from "../../../hooks/usePolarisHash";
import { SubmitButton } from "@codway-dev/ui";

interface FormPreviewProps {
  dropInsertIndex: number | null;
}

export const FormPreview: React.FC<FormPreviewProps> = ({
  dropInsertIndex,
}) => {
  // Global Design Selectors
  const designSettings = useEditorStore(
    (state) => state.config.design.settings
  );

  // Fields Selector
  const fields = useEditorStore((state) => state.config.fields);
  const formName = useEditorStore((state) => state.config.name);
  const shopName = useEditorStore((state) => state.config.shop_name);

  // Hide `offer_embed` fields whose referenced offer no longer exists —
  // the BE keeps the field in the array but drops `resolved_data`, so
  // they'd otherwise render as empty drag-handle rows. The merchant
  // shouldn't see dangling embeds for offers they didn't intentionally
  // place. Stale entries get dropped from the saved payload the next
  // time the merchant edits and saves the form.
  const visibleFields = React.useMemo(
    () =>
      fields.filter(
        (f) => !(f.type === "offer_embed" && f.resolved_data === undefined)
      ),
    [fields]
  );

  // UI State
  const selectedFieldId = useEditorStore((state) => state.ui.selectedFieldId);
  const editorMode = useEditorStore((state) => state.ui.editorMode);

  // Actions
  const setSelectedField = useEditorStore((state) => state.setSelectedField);
  const setSelectedComponent = useEditorStore(
    (state) => state.setSelectedComponent
  );
  const selectedComponentId = useEditorStore(
    (state) => state.ui.selectedComponentId
  );

  // Fallback values for Global Design
  const settingsAny = (designSettings || {}) as DesignSettings;
  const borderRadius = designSettings?.corner_radius ?? 4;
  const primaryColor = designSettings?.primary_color ?? "#008060";
  const backgroundColor = settingsAny.background_color ?? "#FFFFFF";
  const fontFamily = settingsAny.font_family ?? "Inter, sans-serif";
  const fontSize = settingsAny.font_size ?? 16;
  const textColor = settingsAny.text_color ?? "#000000";

  // Container
  const borderColor = settingsAny.border_color ?? "transparent";
  const borderWidth = settingsAny.border_width ?? 0;
  const shadowIntensity = settingsAny.shadow_intensity ?? 0;

  // Fields
  const fieldTextColor = settingsAny.field_text_color ?? "#000000";
  const fieldBackgroundColor = settingsAny.field_background_color ?? "#FFFFFF";
  // Phone prefix keeps a distinct shade by default; themes (e.g. Dark) override.
  const fieldPrefixBg =
    (settingsAny.field_prefix_background_color as string | undefined) ??
    "#f3f4f6";

  // Layout & Logo
  const layoutMode = settingsAny.layout_mode || "embedded";
  const logoUrl = settingsAny.logo_url;
  const showTitle = settingsAny.show_logo === true;

  const template = settingsAny.template_slug || "base";

  const buttonStyleSettings = settingsAny.components?.submit_button || {};
  const buttonBorderRadius = buttonStyleSettings.border_radius ?? borderRadius;
  const buttonBackgroundColor = primaryColor;
  const buttonTextColor = buttonStyleSettings.text_color ?? "#FFFFFF";
  const buttonText =
    buttonStyleSettings.text ||
    settingsAny.submit_button_text ||
    "Completar Orden";

  // Shadow logic
  const getShadow = (intensity: number) => {
    switch (intensity) {
      case 1:
        return "0 1px 3px 0 rgba(0, 0, 0, 0.05)";
      case 2:
        return "0 4px 6px -1px rgba(0, 0, 0, 0.05)";
      case 3:
        return "0 10px 15px -3px rgba(0, 0, 0, 0.05)";
      case 4:
        return "0 20px 25px -5px rgba(0, 0, 0, 0.08)";
      case 5:
        return "0 25px 50px -12px rgba(0, 0, 0, 0.15)";
      default:
        // Sombra por defecto muy suave y amplia tipo "App de Apple"
        return "0 12px 36px -4px rgba(0, 0, 0, 0.06)";
    }
  };

  const sHash = usePolarisHash();

  // Styles for the container based on layout
  const containerStyle: React.CSSProperties &
    Record<string, string | number | undefined> = {
    borderRadius: `${borderRadius}px`,
    backgroundColor: backgroundColor || "#ffffff",
    fontFamily: fontFamily,
    fontSize: `${fontSize}px`,
    color: textColor,
    borderColor: borderWidth === 0 ? "rgba(0,0,0,0.04)" : borderColor,
    borderWidth: borderWidth === 0 ? "1px" : `${borderWidth}px`,
    borderStyle: "solid",
    boxShadow: getShadow(shadowIntensity),
    // CSS Variables for shared @codway-dev/ui components
    "--cod-text-color": textColor,
    "--cod-field-text-color": fieldTextColor,
    "--cod-field-bg-color": fieldBackgroundColor,
    "--cod-field-prefix-bg": fieldPrefixBg,
    "--cod-font-family": fontFamily,
    "--cod-font-size": `${fontSize}px`,
    "--cod-primary-color": primaryColor,
    "--cod-border-radius": `${borderRadius}px`,
    "--cod-border-color": borderColor || "#e0e0e0",
    "--submit-btn-bg-color": buttonBackgroundColor,
    "--submit-btn-text-color": buttonTextColor,
    "--submit-btn-border-radius": `${buttonBorderRadius}px`,

    // CSS Variables for Polaris Custom Elements
    "--p-color-text": textColor,
    "--p-color-border": borderColor,
    "--p-border-radius-200": `${borderRadius}px`,

    // Shopify Web Components (CDN polaris.js) specific variables
    [`--s-global-font-family-${sHash}`]: fontFamily,
    [`--s-global-font-size-${sHash}`]: `${fontSize}px`,
    [`--s-global-color-${sHash}`]: textColor,
    [`--s-icon-color-${sHash}`]: textColor,
    [`--s-input-field-label-color-${sHash}`]: textColor,
    [`--s-input-field-label-font-size-${sHash}`]: `${fontSize}px`,
    [`--s-heading-font-weight-${sHash}`]: "bold",

    // Typography Fonts
    "--p-font-family-sans": fontFamily,
    "--p-font-family-mono": fontFamily,
    "--p-text-body-xs-font-family": fontFamily,
    "--p-text-body-sm-font-family": fontFamily,
    "--p-text-body-md-font-family": fontFamily,
    "--p-text-body-lg-font-family": fontFamily,
    "--p-text-heading-xs-font-family": fontFamily,
    "--p-text-heading-sm-font-family": fontFamily,
    "--p-text-heading-md-font-family": fontFamily,
    "--p-text-heading-lg-font-family": fontFamily,
    "--p-text-heading-xl-font-family": fontFamily,
    "--p-text-heading-2xl-font-family": fontFamily,
    "--p-text-heading-3xl-font-family": fontFamily,
  };

  if (layoutMode === "popup") {
    containerStyle.boxShadow = "0 25px 50px -12px rgba(0, 0, 0, 0.25)";
    containerStyle.maxWidth = "480px";
  }

  // Local State for validation and results
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [successData, setSuccessData] = React.useState<string | null>(null);

  // Handle field click
  const handleFieldClick = (e: React.MouseEvent, fieldId: string) => {
    e.stopPropagation();
    if (editorMode === "preview") return;
    setSelectedField(fieldId);
  };

  const validateField = (field: EditorField, value: string) => {
    if (field.required && !value) return "This field is required";
    if (value) {
      if (field.type === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return "Invalid email address";
      }
      if (field.type === "tel") {
        const phoneRegex = /^\+?[\d\s-]{10,}$/;
        if (!phoneRegex.test(value)) return "Invalid phone number";
      }
    }
    return null;
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (editorMode === "preview") return;
    setSelectedComponent("submit_button");
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setErrors({});
    setSuccessData(null);

    const newErrors: Record<string, string> = {};
    const formData: Record<string, string> = {};

    fields.forEach((field) => {
      const value = field.defaultValue || "";
      const error = validateField(field, value);
      if (error) newErrors[field.tempId] = error;
      formData[field.label] = value;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSuccessData(JSON.stringify(formData, null, 2));
  };

  return (
    <div
      style={containerStyle}
      className={`w-full max-w-2xl min-h-[600px] p-8 flex flex-col items-center justify-start text-center transition-all relative cursor-default ${
        !selectedFieldId && !selectedComponentId && editorMode === "edit"
          ? "ring-2 ring-indigo-500/20"
          : ""
      }`}
    >
      {/* HEADER: Logo or Title */}
      <div className="w-full mb-6">
        {showTitle &&
          (logoUrl ? (
            <div className="flex justify-center mb-4">
              <img
                src={logoUrl}
                alt="Logo Preview"
                className="max-h-16 object-contain"
              />
            </div>
          ) : (
            <div className="text-center mb-4 font-bold text-xl text-gray-800">
              {shopName || "Nombres de la Tienda"}
            </div>
          ))}
      </div>

      {/* Brand Template Decorative Header */}
      {template === "brand" && (
        <div
          style={{
            width: "100%",
            height: "8px",
            backgroundColor: primaryColor,
            position: "absolute",
            top: 0,
            left: 0,
            borderTopLeftRadius: `${borderRadius}px`,
            borderTopRightRadius: `${borderRadius}px`,
          }}
        />
      )}

      <s-heading>
        {(settingsAny as DesignSettings).form_title || formName || "Preview"}
      </s-heading>

      {/* Form Content Container */}
      <div className="w-full text-left mt-4 flex flex-col space-y-0 codway-form-preview">
        {dropInsertIndex === 0 && (
          <div className="w-full h-12 border-2 border-dashed border-indigo-400 bg-indigo-50/50 rounded-lg flex items-center justify-center text-indigo-500 font-medium shadow-sm transition-all mb-2">
            Suelta el campo aquí
          </div>
        )}
        {visibleFields.length === 0 ? (
          <div className="text-center text-gray-400 py-12 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/50">
            <s-text color="subdued">Arrastra campos aquí para empezar</s-text>
          </div>
        ) : (
          <SortableContext
            items={visibleFields.map((f) => f.tempId)}
            strategy={verticalListSortingStrategy}
          >
            {visibleFields.map((field, index) => (
              <React.Fragment key={field.tempId}>
                <SortableField
                  field={field}
                  isSelected={selectedFieldId === field.tempId}
                  editorMode={editorMode}
                  onClick={(e) => handleFieldClick(e, field.tempId)}
                  error={errors[field.tempId]}
                  style={{
                    color: fieldTextColor,
                  }}
                />
                {dropInsertIndex === index + 1 && (
                  <div className="w-full h-12 border-2 border-dashed border-indigo-400 bg-indigo-50/50 rounded-lg flex items-center justify-center text-indigo-500 font-medium shadow-sm transition-all mb-2">
                    Suelta el campo aquí
                  </div>
                )}
              </React.Fragment>
            ))}
          </SortableContext>
        )}
      </div>

      {/* Submit Button Preview */}
      <div className="w-full mt-6 space-y-4">
        {successData && (
          <div className="w-full p-4 bg-green-50 border border-green-200 rounded text-left">
            <h4 className="font-bold text-green-800 mb-2">
              Form Validated Successfully!
            </h4>
            <pre className="text-xs text-green-700 overflow-auto bg-white p-2 rounded border border-green-100">
              {successData}
            </pre>
          </div>
        )}

        <div
          onClick={handleButtonClick}
          className={`relative w-full transition-all rounded-lg cursor-pointer group ${
            selectedComponentId === "submit_button" && editorMode === "edit"
              ? "ring-2 ring-indigo-500/80 bg-indigo-50/30"
              : editorMode === "edit"
                ? "hover:ring-1 hover:ring-gray-300"
                : ""
          }`}
        >
          <div
            style={{ pointerEvents: editorMode === "edit" ? "none" : "auto" }}
          >
            <SubmitButton
              label={buttonText}
              type="button"
              onClick={handleSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
